import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { Products } from '@/shared/types/Billing';

const QUERY_KEY = ['products'];

const getProducts = async () => {
  const response = await API.getPlans();

  const sortedPlans = response.plan.sort((a, b) => a.metadata.position - b.metadata.position);

  return {
    boosters: response.boosters,
    credit: response.credit,
    plan: sortedPlans,
  };
};

type UseProductsOptions = {
  enableFallback?: boolean;
  showErrors?: boolean;
};

export const useProducts = (enabled = false, options: UseProductsOptions = {}) => {
  const { enableFallback = false, showErrors = true } = options;

  const { data, error, isLoading, isSuccess } = useQuery<Products>({
    enabled,
    queryFn: getProducts,
    queryKey: QUERY_KEY,
    refetchOnMount: false,
    retry: (failureCount, retryError) => {
      if (failureCount >= 2) return false;

      const status = (retryError as any)?.response?.status;
      if (status === 429) return failureCount < 1;

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(5000 * 2 ** attemptIndex, 30_000),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (error && showErrors) {
      // Don't show error alerts for rate limiting
      const status = (error as any)?.response?.status;
      if (status !== 429) {
        showErrorAlert(error);
      }
    }
  }, [error, showErrors]);

  const fallbackData = useMemo(
    () => (enableFallback ? { boosters: [], credit: [], plan: [] } : undefined),
    [enableFallback],
  );

  const products = data ?? fallbackData;

  return {
    error,
    isLoading,
    isSuccess,
    products,
  };
};
